import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { THEME_PREFERENCES } from '@/lib/themePreference';
import { ApiCallError, callFunction } from '@/lib/api';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { Card } from '@/components/ui/Card';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { StatusChip } from '@/components/ui/Chip';
import { PageLayout } from '@/components/ui/PageLayout';
import { TYPE } from '@/lib/typography';
import toast from 'react-hot-toast';
import { Smartphone, ChevronRight, Globe, Moon } from 'lucide-react';
import { cn } from '@/utils/cn';

const ROLE_LABELS: Record<string, { da: string; en: string }> = {
  main_clinic_admin: { da: 'Hoved-administrator', en: 'Main administrator' },
  clinic_admin: { da: 'Administrator', en: 'Administrator' },
  employee: { da: 'Medarbejder', en: 'Employee' },
};

export function AccountSecurity() {
  const { t, i18n } = useTranslation();
  const { user, memberships, primaryClinicId, isPlatformAdmin } = useAuth();
  const { preference, setPreference } = useTheme();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (next.length < 8) {
      setError(t('auth.reset_password.min_length'));
      return;
    }
    if (next !== confirm) {
      setError(t('auth.reset_password.mismatch'));
      return;
    }
    if (next === current) {
      setError(t('account.security.same_password'));
      return;
    }

    setSubmitting(true);
    try {
      await callFunction<{ current_password: string; new_password: string }, { changed: true }>(
        'change-password',
        {
          current_password: current,
          new_password: next,
        },
      );
      toast.success(t('account.security.changed_toast'));
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      if (err instanceof ApiCallError) {
        if (err.code === 'unauthorized') {
          setError(t('account.security.wrong_current'));
        } else if (err.code === 'invalid-input') {
          setError(t('account.security.invalid'));
        } else {
          setError(t('auth.errors.unknown'));
        }
      } else {
        setError(t('auth.errors.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const lang = i18n.language === 'en' ? 'en' : 'da';

  return (
    <PageLayout title={t('account.security.title')} subtitle={user?.email} contentClassName="flex flex-col gap-s4">

      <Card>
        <h2 className={cn(TYPE.heading, 'text-ds-text')}>
          {t('account.security.active_sessions')}
        </h2>
        <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
          {t('account.security.active_sessions_hint')}
        </p>
        <Link
          to="/account/sessions"
          className="mt-s4 flex items-center justify-between rounded-ctl bg-ds-sunk p-s4"
        >
          <div className="flex items-center gap-s3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ds-teal-tint">
              <Smartphone className="h-5 w-5 text-ds-teal" aria-hidden />
            </div>
            <div>
              <div className={cn(TYPE.section, 'text-ds-text')}>
                {t('account.security.manage_sessions')}
              </div>
              <div className={cn(TYPE.small, 'text-ds-muted')}>
                {t('account.security.view_devices')}
              </div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-ds-muted" aria-hidden />
        </Link>
      </Card>

      <Card>
        <h2 className={cn(TYPE.heading, 'text-ds-text')}>
          {t('account.security.change_password')}
        </h2>
        <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
          {t('account.security.change_password_hint')}
        </p>

        {error ? (
          <div className="mt-s4">
            <ErrorAlert message={error} />
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="mt-s5 flex flex-col gap-s4">
          <PasswordInput
            label={t('account.security.current_password')}
            name="current"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
          <PasswordInput
            label={t('account.security.new_password')}
            name="new"
            autoComplete="new-password"
            required
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <PasswordInput
            label={t('account.security.confirm_password')}
            name="confirm"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              className="w-full nav:w-auto"
              loading={submitting}
              loadingText={t('account.security.saving')}
            >
              {t('account.security.save')}
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <div className="flex items-center gap-s3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ds-teal-tint">
            <Moon className="h-5 w-5 text-ds-teal" aria-hidden />
          </div>
          <div>
            <h2 className={cn(TYPE.heading, 'text-ds-text')}>
              {t('account.security.appearance')}
            </h2>
            <p className={cn(TYPE.small, 'text-ds-muted')}>{t('account.security.appearance_hint')}</p>
          </div>
        </div>
        <div className="mt-s4 grid grid-cols-1 gap-s3 nav:grid-cols-3" role="radiogroup" aria-label={t('account.security.appearance')}>
          {THEME_PREFERENCES.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={preference === value}
              onClick={() => setPreference(value)}
              className={cn(
                TYPE.control,
                'min-h-10 rounded-ctl border px-s4 py-s3 font-medium',
                preference === value
                  ? 'border-ds-teal-line bg-ds-teal-tint text-ds-teal'
                  : 'border-ds-field bg-ds-card text-ds-text hover:bg-ds-neutral-tint',
              )}
            >
              {t(`account.security.theme_${value}` as const)}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        <div className="flex items-center gap-s3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ds-teal-tint">
            <Globe className="h-5 w-5 text-ds-teal" aria-hidden />
          </div>
          <div>
            <h2 className={cn(TYPE.heading, 'text-ds-text')}>
              {t('account.security.language')}
            </h2>
            <p className={cn(TYPE.small, 'text-ds-muted')}>{t('account.security.language_hint')}</p>
          </div>
        </div>
        <div className="mt-s4 grid grid-cols-1 gap-s3 nav:grid-cols-2">
          <button
            type="button"
            onClick={() => {
              void i18n.changeLanguage('da');
              toast.success(t('account.security.language_saved'));
            }}
            className={cn(
              TYPE.control,
              'min-h-10 rounded-ctl border px-s4 py-s3 font-medium',
              i18n.language !== 'en'
                ? 'border-ds-teal-line bg-ds-teal-tint text-ds-teal'
                : 'border-ds-field bg-ds-card text-ds-text hover:bg-ds-neutral-tint',
            )}
          >
            {t('account.security.language_da')}
          </button>
          <button
            type="button"
            onClick={() => {
              void i18n.changeLanguage('en');
              toast.success(t('account.security.language_saved'));
            }}
            className={cn(
              TYPE.control,
              'min-h-10 rounded-ctl border px-s4 py-s3 font-medium',
              i18n.language === 'en'
                ? 'border-ds-teal-line bg-ds-teal-tint text-ds-teal'
                : 'border-ds-field bg-ds-card text-ds-text hover:bg-ds-neutral-tint',
            )}
          >
            {t('account.security.language_en')}
          </button>
        </div>
      </Card>

      <Card>
        <h2 className={cn(TYPE.heading, 'text-ds-text')}>
          {t('account.security.memberships')}
        </h2>
        <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
          {t('account.security.memberships_hint')}
        </p>

        {isPlatformAdmin ? (
          <Banner tone="warning" className="mt-s4">
            {t('account.security.platform_admin_badge')}
          </Banner>
        ) : null}

        {memberships.length === 0 ? (
          <p className={cn(TYPE.body, 'mt-s3 text-ds-muted')}>
            {t('account.security.no_memberships')}
          </p>
        ) : (
          <ul className="mt-s3 divide-y divide-ds-line">
            {memberships.map((m) => (
              <li key={m.clinic_id} className="flex items-center justify-between gap-s3 py-s3">
                <div className="min-w-0">
                  <div className={cn(TYPE.body, 'font-mono text-ds-text')}>
                    {m.clinic_id.slice(0, 8)}…
                  </div>
                  <div className={cn(TYPE.small, 'text-ds-muted')}>
                    {ROLE_LABELS[m.role]?.[lang] ?? m.role}
                  </div>
                </div>
                {primaryClinicId === m.clinic_id ? (
                  <StatusChip tone="upcoming">{t('account.security.primary_clinic')}</StatusChip>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </PageLayout>
  );
}
